import { useQueryClient } from '@tanstack/react-query'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { useSettings } from '@datasets/dataset-details/hooks/useSettings'
import { IDatasetById } from '@global/api/transforms'
import { useCreativeClassifications } from '@global/hooks/queries/useCreativeClassifications'
import {
  DEFAULT_PARAMS,
  KEY as DATASETS_QUERY_KEY,
} from '@global/hooks/queries/useDatasetsDataQuery'
import { useAppSelector } from '@redux/hooks'
import { KEY } from './useDatasetsDataQueryObserver'

/**
 * Custom hook to manage dataset data cache using React Query's query client.
 *
 * @returns {Object} Contains methods to interact with the dataset data cache.
 *
 * @example
 * const { addDatasetDataById } = useDatasetsDataCache();
 * addDatasetDataById(updatedData);
 */
export const useDatasetsDataCache = () => {
  const queryClient = useQueryClient()
  const { data: classifications = [] } = useCreativeClassifications()
  const { requireApproval } = useSettings()
  const [APPROVAL_ENABLED = false] = useAppSelector(selectFeatureIncludes([FeatureDict.APPROVAL]))

  /**
   * Merges the provided dataset data with the current data in the cache
   * and updates the cache with the merged data.
   *
   * @param {Object} updated - The dataset data to be added/merged with the current cache.
   */
  const addDatasetDataById = (updated: IDatasetById) => {
    // Get the current data from the query client
    const currentData = queryClient.getQueryData<string[]>([KEY]) ?? {}

    // Merge current data with new rows
    const updatedData = { ...currentData, ...updated }

    // Set the merged data back into the query client
    queryClient.setQueryData([KEY], updatedData)
  }

  const invalidateCache = ({ datasetId }) => {
    const hasCommentSupport = APPROVAL_ENABLED && requireApproval

    void queryClient.invalidateQueries({
      queryKey: [
        DATASETS_QUERY_KEY,
        {
          ...DEFAULT_PARAMS,
          datasetId,
          hasCommentSupport,
          refetchDatasetNum: 1,
          refetchDatasetRefNum: 1,
          classificationCategoryIds: classifications.map(({ id }) => id),
        },
      ],
    })

    void queryClient.invalidateQueries({ queryKey: [KEY] })
  }

  return {
    addDatasetDataById,
    invalidateCache,
  }
}
